'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { PDF_EXTRACT_MAX_ATTEMPTS, PDF_MAX_SIZE_BYTES } from '@/app/constants/loanPdfExtract'
import { useLoanPdfExtract } from '@/app/hooks/useLoanPdfExtract'
import LoanPdfExtractDropzone from './LoanPdfExtractDropzone'
import LoanPdfExtractProgress from './LoanPdfExtractProgress'
import LoanPdfExtractSuccess from './LoanPdfExtractSuccess'
import LoanPdfExtractError from './LoanPdfExtractError'
import LoanPdfHowItWorksModal from './LoanPdfHowItWorksModal'

interface Props {
    extract: ReturnType<typeof useLoanPdfExtract>
    locked: boolean
    signatureDate?: string
    importedCount: number
    onFileAccepted: (file: File) => void
    onFileRejected: (code: string) => void
    onManualEntry: () => void
    onHelpOpen: () => void
}

export default function LoanPdfExtractBlock({
    extract,
    locked,
    signatureDate,
    importedCount,
    onFileAccepted,
    onFileRejected,
    onManualEntry,
    onHelpOpen,
}: Props) {
    const [showHelp, setShowHelp] = useState(false)
    const { status, file, fileName, fileSize, errorCode, elapsedMs, stepIndex, progress, successQuote, attempts } =
        extract

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        accept: { 'application/pdf': ['.pdf'] },
        maxSize: PDF_MAX_SIZE_BYTES,
        multiple: false,
        noClick: true,
        noKeyboard: true,
        disabled: locked,
        onDropAccepted: (files) => onFileAccepted(files[0]),
        onDropRejected: (rejections) => {
            const code = rejections[0]?.errors?.[0]?.code
            onFileRejected(code === 'file-too-large' ? 'FILE_TOO_LARGE' : 'FILE_INVALID_TYPE')
        },
    })

    const handleHelpClick = useCallback(() => {
        onHelpOpen()
        setShowHelp(true)
    }, [onHelpOpen])

    const handleReplace = useCallback(() => {
        extract.cancel()
        open()
    }, [extract, open])

    const objectUrlRef = useRef<string | null>(null)
    useEffect(
        () => () => {
            if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
        },
        []
    )

    const handleViewDocument = () => {
        if (!file) return
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = URL.createObjectURL(file)
        window.open(objectUrlRef.current, '_blank', 'noopener')
    }

    const attemptsLeft = PDF_EXTRACT_MAX_ATTEMPTS - attempts

    return (
        <div className="mb-6">
            <input {...getInputProps()} data-test="pdf-extract-input" />

            {status === 'analyzing' && (
                <LoanPdfExtractProgress
                    fileName={fileName || ''}
                    fileSize={fileSize}
                    stepIndex={stepIndex}
                    elapsedMs={elapsedMs}
                    progressRatio={progress.ratio}
                    remainingSeconds={progress.remainingSeconds}
                    isOvertime={progress.isOvertime}
                    onReplace={handleReplace}
                />
            )}

            {status === 'success' && (
                <LoanPdfExtractSuccess
                    bankName={successQuote?.bank_name || successQuote?.loans?.[0]?.bank?.name}
                    loansCount={successQuote?.loans?.length ?? 0}
                    importedCount={importedCount}
                    signatureDate={signatureDate}
                    onViewDocument={file ? handleViewDocument : undefined}
                    onReimport={extract.reset}
                    onManualEntry={onManualEntry}
                    onHelpClick={handleHelpClick}
                />
            )}

            {status === 'error' && (
                <LoanPdfExtractError code={errorCode} rootProps={getRootProps()} onRetry={extract.retry} />
            )}

            {status === 'idle' && (
                <LoanPdfExtractDropzone
                    rootProps={getRootProps()}
                    isDragActive={isDragActive}
                    disabled={locked}
                    onChoose={open}
                />
            )}

            {status !== 'success' && status !== 'analyzing' && (
                <p className="m-0 mt-3 flex flex-wrap items-center gap-1.5 text-[13px] text-pale/70">
                    <i className="fa-regular fa-shield" />
                    Document analysé automatiquement, supprimé juste après extraction.
                    <button type="button" onClick={handleHelpClick} data-test="pdf-extract-help" className="underline">
                        Comment ça marche
                    </button>
                    {attempts > 0 && attemptsLeft > 0 && (
                        <span data-test="pdf-extract-attempts">
                            · Il vous reste {attemptsLeft} analyse{attemptsLeft > 1 ? 's' : ''} pour ce devis.
                        </span>
                    )}
                </p>
            )}

            {showHelp && <LoanPdfHowItWorksModal onClose={() => setShowHelp(false)} />}
        </div>
    )
}
